import { createReducer, on } from "@ngrx/store";
import {
  Organization,
  OrganizationChatScope,
} from "src/app/shared/interfaces/organization";
import {
  organizationChatSettingsLoad,
  organizationChatSettingsLoadFail,
  organizationChatSettingsLoadSuccess,
  organizationChatSettingsUpdate,
  organizationChatSettingsUpdateFail,
  organizationChatSettingsUpdateSuccess,
  organizationsListLoad,
  organizationsListLoadFail,
  organizationsListLoadSuccess,
} from "../actions/organizations.actions";
import { HttpErrorResponse } from "@angular/common/http";
import { Guid } from "src/app/shared/types/guid";

export interface OrganizationChatSettingsState {
  organizationId: Guid | null;
  chatScope: OrganizationChatScope;
  chatEnabled: boolean;
  loaded: boolean;
  loading: boolean;
  saving: boolean;
  error: HttpErrorResponse | null;
}

export interface OrganizationsListState {
  organizations: Organization[];
  error: HttpErrorResponse | null;
  chatSettings: OrganizationChatSettingsState;
}

const initialState: OrganizationsListState = {
  organizations: [],
  error: null,
  chatSettings: {
    organizationId: null,
    chatScope: "group",
    chatEnabled: false,
    loaded: false,
    loading: false,
    saving: false,
    error: null,
  },
};

export const organizationsListReducer = createReducer(
  initialState,
  on(
    organizationsListLoad,
    (state): OrganizationsListState => ({
      ...state,
      error: null,
    }),
  ),
  on(
    organizationsListLoadSuccess,
    (state, action): OrganizationsListState => ({
      ...state,
      organizations: action.organizations,
      error: null,
    }),
  ),
  on(
    organizationsListLoadFail,
    (state, { error }): OrganizationsListState => ({
      ...state,
      error,
    }),
  ),
  on(
    organizationChatSettingsLoad,
    (state, { organizationId }): OrganizationsListState => ({
      ...state,
      chatSettings: {
        organizationId,
        chatScope: "group",
        chatEnabled: false,
        loaded: false,
        loading: true,
        saving: false,
        error: null,
      },
    }),
  ),
  on(
    organizationChatSettingsLoadSuccess,
    (
      state,
      { organizationId, chatScope, chatEnabled },
    ): OrganizationsListState =>
      state.chatSettings.organizationId !== organizationId
        ? state
        : {
            ...state,
            chatSettings: {
              ...state.chatSettings,
              chatScope,
              chatEnabled,
              loading: false,
              loaded: true,
              error: null,
            },
          },
  ),
  on(
    organizationChatSettingsLoadFail,
    (state, { organizationId, error }): OrganizationsListState =>
      state.chatSettings.organizationId !== organizationId
        ? state
        : {
            ...state,
            chatSettings: { ...state.chatSettings, loading: false, error },
          },
  ),
  on(
    organizationChatSettingsUpdate,
    (
      state,
      { organizationId, chatScope, chatEnabled },
    ): OrganizationsListState =>
      state.chatSettings.organizationId !== organizationId
        ? state
        : {
            ...state,
            chatSettings: {
              ...state.chatSettings,
              chatScope,
              chatEnabled,
              saving: true,
              error: null,
            },
          },
  ),
  on(
    organizationChatSettingsUpdateSuccess,
    (
      state,
      { organizationId, chatScope, chatEnabled },
    ): OrganizationsListState =>
      state.chatSettings.organizationId !== organizationId
        ? state
        : {
            ...state,
            chatSettings: {
              ...state.chatSettings,
              chatScope,
              chatEnabled,
              saving: false,
              error: null,
            },
          },
  ),
  on(
    organizationChatSettingsUpdateFail,
    (
      state,
      { organizationId, previousChatScope, previousChatEnabled, error },
    ): OrganizationsListState =>
      state.chatSettings.organizationId !== organizationId
        ? state
        : {
            ...state,
            chatSettings: {
              ...state.chatSettings,
              chatScope: previousChatScope,
              chatEnabled: previousChatEnabled,
              saving: false,
              error,
            },
          },
  ),
);
